// Columna central del explorer: las 7 columnas del mockup, pintadas con el `DataTable` compartido.
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { TierPill } from '@/shared/patterns/Pills';
import {
    arrearsLabel,
    arrearsTone,
    formatMoneyCents,
    lastContactCell,
    promiseCell,
} from '../logic';
import { CUSTOMER_COLUMN_LABELS, type CollectorCustomerRow } from '../types';
import { ToneChip } from './ToneChip';

/** Las 6 columnas fijas suman 418px y Cliente pide el resto. Por debajo, la tabla scrollea. */
const TABLE_MIN_WIDTH = '620px';

const [CL, CLIENT, TIER, DPI, ARREARS, PROMISE, LAST_CONTACT] = CUSTOMER_COLUMN_LABELS;

const COLUMNS: DataTableColumn<CollectorCustomerRow>[] = [
    {
        key: 'id',
        label: CL,
        width: '72px',
        // eslint-disable-next-line no-restricted-syntax -- columna «CL» del mockup: el folio ES la columna
        cell: (row) => <span className="font-mono text-[10.5px]">{row.id}</span>,
    },
    { key: 'name', label: CLIENT, cell: (row) => row.name },
    {
        key: 'tier',
        label: TIER,
        width: '48px',
        cellOverflow: 'nowrap',
        cell: (row) => <TierPill level={row.tier} label="abbr" />,
    },
    {
        key: 'arrearsDays',
        label: DPI,
        width: '52px',
        cellOverflow: 'nowrap',
        cell: (row) => (
            <ToneChip tone={arrearsTone(row.worstArrearsDays)}>
                {arrearsLabel(row.worstArrearsDays)}
            </ToneChip>
        ),
    },
    {
        key: 'arrearsAmount',
        label: ARREARS,
        width: '78px',
        numeric: true,
        cell: (row) => (
            <span className="font-semibold">{formatMoneyCents(row.totalArrearsCents)}</span>
        ),
    },
    {
        key: 'promise',
        label: PROMISE,
        width: '88px',
        cellOverflow: 'nowrap',
        cell: (row) => {
            const cell = promiseCell(row);
            if (cell === null) return <span className="text-[10px] text-muted-foreground">—</span>;
            return <ToneChip tone={cell.tone}>{cell.label}</ToneChip>;
        },
    },
    {
        key: 'lastContact',
        label: LAST_CONTACT,
        width: '80px',
        cellOverflow: 'nowrap',
        cell: (row) => {
            const cell = lastContactCell(row);
            return <ToneChip tone={cell.tone}>{cell.label}</ToneChip>;
        },
    },
];

export interface CustomersTableProps {
    rows: readonly CollectorCustomerRow[];
    selectedId: string | null;
    onSelect: (id: string) => void;
}

export function CustomersTable({ rows, selectedId, onSelect }: CustomersTableProps) {
    return (
        <DataTable
            columns={COLUMNS}
            rows={rows}
            rowKey={(row) => row.id}
            caption="Clientes asignados al cobrador"
            minWidth={TABLE_MIN_WIDTH}
            onRowClick={(row) => {
                onSelect(row.id);
            }}
            isRowSelected={(row) => row.id === selectedId}
            empty="Ningún cliente coincide con los filtros."
        />
    );
}
